import { useMemo, useState, useEffect, useRef, Fragment } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, ArrowUp, ArrowDown, Edit3, MessageSquare, X, Wallet, WrapText } from "lucide-react";
import { symbolFor, fmtMoney, CURRENCIES } from "./TaskFinanceDialog";

// R159y · Feb 2026 — Finance Report dialog. Full-screen searchable /
// sortable list of tasks with their est / paid / balance, group-by
// dropdown (none / project / brand / client) that switches between
// per-task rows and aggregated sub-total rows. Includes a CSV export
// of whatever's currently on screen.
//
// All aggregation happens client-side against the already-loaded tasks
// so opening the report is instant — no new backend contract.
function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCSV(rows, filename) {
  const blob = new Blob([rows.map((r) => r.map(csvEscape).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// R159dd · Feb 2026 — Absolute drag handle rendered on the right edge
// of every finance-report column header. `onMouseDown` starts the drag
// (parent tracks the active column via a ref and mounts document-level
// mousemove/mouseup listeners). The 6px hit target is invisible until
// hovered so headers stay clean.
function ResizeHandle({ onMouseDown, onReset, testid }) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      onMouseDown={onMouseDown}
      onClick={(e) => { e.stopPropagation(); }}
      onDoubleClick={(e) => { e.stopPropagation(); onReset?.(); }}
      className="absolute top-0 right-0 h-full w-[6px] cursor-col-resize select-none z-20 hover:bg-amber-400/60 active:bg-amber-500/80"
      data-testid={`finance-report-${testid}`}
      title="Drag to resize · double-click to reset"
    />
  );
}

// R159y · Feb 2026 — Column header with three-state sort indicator.
// Extracted so the parent component doesn't re-declare it on every
// render (react/no-unstable-nested-components).
function SortHead({ sortKey, activeKey, sortDir, onSet, right, children }) {
  const active = activeKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSet(sortKey)}
      className={`px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1 ${right ? "justify-end w-full" : ""}`}
      data-testid={`finance-report-sort-${sortKey}`}
    >
      {children}
      {active && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
    </button>
  );
}

// R159z · Feb 2026 — Compact multi-select facet filter. Renders a
// popover of toggleable option chips. Empty set = "any". Chips inside
// the popover show a checkmark when selected. Extracted so the parent
// dialog doesn't redeclare it on every render.
// R159bb · Feb 2026 — Added a search input on top of the option list
// so long client/brand/project lists stay usable. Options are always
// rendered sorted; the search does a case-insensitive substring match.
function FacetFilter({ label, values, selected, onToggle, testid, searchable }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  if (!values || values.length === 0) return null;
  const active = selected && selected.size > 0;
  const filtered = searchable && q.trim()
    ? values.filter((v) => String(v).toLowerCase().includes(q.trim().toLowerCase()))
    : values;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`h-8 px-2 border rounded-none text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${active ? "bg-amber-500 text-white border-amber-600" : "border-amber-300 bg-white text-amber-700 hover:bg-amber-50"}`}
        data-testid={testid}
      >
        {label}{active ? ` · ${selected.size}` : ""}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute top-full left-0 mt-1 z-40 min-w-[220px] max-w-[320px] border border-amber-300 bg-white shadow-lg"
            data-testid={`${testid}-menu`}
          >
            {searchable && (
              <div className="p-1 border-b border-border">
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${label.toLowerCase()}…`}
                  className="w-full h-7 px-2 border border-neutral-200 text-[11px] focus:outline-none focus:border-amber-400"
                  data-testid={`${testid}-search`}
                />
              </div>
            )}
            <div className="max-h-[240px] overflow-auto py-1">
              {filtered.length === 0 && <div className="px-2 py-2 text-[10px] text-muted-foreground italic">No matches</div>}
              {filtered.map((v) => {
                const on = selected.has(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => onToggle(v)}
                    className={`w-full text-left px-2 py-1 text-[11px] flex items-center gap-2 hover:bg-amber-50 ${on ? "font-semibold text-amber-800" : ""}`}
                    data-testid={`${testid}-opt-${String(v).replace(/[^A-Za-z0-9_-]+/g, "_")}`}
                  >
                    <span className={`w-3 h-3 border ${on ? "bg-amber-500 border-amber-600" : "border-neutral-300"} inline-flex items-center justify-center text-white text-[9px]`}>
                      {on ? "✓" : ""}
                    </span>
                    <span className="truncate">{v}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// R159bb · Feb 2026 — Reusable inline-edit cell. Renders the read
// value; on click swaps to an input/select for the given `kind`;
// Enter or blur commits, Escape cancels. Numeric/date/select kinds
// supported. When onPatchTask is missing (permission-gated) falls
// back to a read-only cell. Module-scope to avoid react/no-unstable-
// nested-components.
function InlineCell({ taskId, field, value, kind = "text", options = null, className = "", render, cellTestid, onPatchTask }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  if (!onPatchTask) {
    return <td className={className} data-testid={cellTestid}>{render ? render(value) : (value ?? "—")}</td>;
  }
  const commit = () => {
    let out = draft;
    if (kind === "number") out = draft === "" ? null : Number(draft);
    if (kind === "text" && String(draft).trim() === "") out = "";
    if (out !== value) onPatchTask(taskId, { [field]: out });
    setEditing(false);
  };
  if (!editing) {
    // R159gg · Feb 2026 — When rendering a `kind="select"` cell in
    // read-only mode, prefer the option's human label over the raw
    // stored value (e.g. show "In Progress" not "in_progress"). Any
    // `render` prop wins so bespoke rendering (chips, formatting) is
    // preserved.
    const displayValue = (() => {
      if (render) return render(value);
      if (value === "" || value == null) return <span className="text-neutral-300">—</span>;
      if (kind === "select" && Array.isArray(options)) {
        const hit = options.find((o) => (o.value ?? o) === value);
        if (hit) return hit.label ?? hit;
      }
      return value;
    })();
    return (
      <td
        className={`${className} cursor-text hover:bg-amber-50`}
        onClick={() => { setDraft(value ?? ""); setEditing(true); }}
        title="Click to edit"
        data-testid={cellTestid}
      >
        {displayValue}
      </td>
    );
  }
  if (kind === "select") {
    return (
      <td className={className} data-testid={cellTestid}>
        <select
          autoFocus
          value={draft ?? ""}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          className="w-full h-6 border border-amber-400 px-1 text-[11px] rounded-none"
        >
          {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
        </select>
      </td>
    );
  }
  return (
    <td className={className} data-testid={cellTestid}>
      <input
        autoFocus
        type={kind === "number" ? "number" : kind === "date" ? "date" : "text"}
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        className="w-full h-6 border border-amber-400 px-1 text-[11px] rounded-none font-mono"
        step={kind === "number" ? "0.01" : undefined}
        min={kind === "number" ? "0" : undefined}
      />
    </td>
  );
}

export default function FinanceReportDialog({ open, onClose, tasks, projects, onOpenTask, onOpenNotes, onPatchTask, onOpenFinance, wsId }) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("balance");
  const [sortDir, setSortDir] = useState("desc");
  const [groupBy, setGroupBy] = useState("none");
  const [statusFilter, setStatusFilter] = useState(() => new Set());
  const [currencyFilter, setCurrencyFilter] = useState(() => new Set());
  const [brandFilter, setBrandFilter] = useState(() => new Set());
  const [billingFilter, setBillingFilter] = useState(() => new Set());
  const [clientFilter, setClientFilter] = useState(() => new Set());
  const [projectFilter, setProjectFilter] = useState(() => new Set());
  const [wrap, setWrap] = useState(false);
  // R159cc · Feb 2026 — Filter presets (localStorage-backed per ws).
  const presetKey = wsId ? `co_finance_presets_${wsId}` : null;
  const [presets, setPresets] = useState(() => {
    if (!presetKey) return [];
    try { return JSON.parse(localStorage.getItem(presetKey) || "[]"); } catch { return []; }
  });
  const [activePreset, setActivePreset] = useState(null);
  const savePresets = (next) => {
    setPresets(next);
    if (presetKey) try { localStorage.setItem(presetKey, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const currentFilterSnapshot = () => ({
    q, groupBy,
    status: [...statusFilter], currency: [...currencyFilter], brand: [...brandFilter],
    billing: [...billingFilter], client: [...clientFilter], project: [...projectFilter],
  });
  const applyPreset = (p) => {
    setQ(p.q || "");
    setGroupBy(p.groupBy || "none");
    setStatusFilter(new Set(p.status || []));
    setCurrencyFilter(new Set(p.currency || []));
    setBrandFilter(new Set(p.brand || []));
    setBillingFilter(new Set(p.billing || []));
    setClientFilter(new Set(p.client || []));
    setProjectFilter(new Set(p.project || []));
    setActivePreset(p.name);
  };
  const saveCurrentAsPreset = () => {
    const name = window.prompt("Preset name (e.g. 'Unpaid & Delivered')");
    if (!name) return;
    const next = [...presets.filter((p) => p.name !== name), { name, ...currentFilterSnapshot() }];
    savePresets(next);
    setActivePreset(name);
    toast.success(`Preset "${name}" saved`);
  };
  const deletePreset = (name) => {
    savePresets(presets.filter((p) => p.name !== name));
    if (activePreset === name) setActivePreset(null);
  };
  // R159cc · Feb 2026 — Bulk-billing toolbar state.
  // R159ee · Feb 2026 — Live progress + cancel. `bulkProgress` is set
  // while the loop is running (null otherwise). A ref carries the
  // cancel flag so the async loop can bail out mid-run without
  // waiting for React state to propagate.
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {done, total, state}
  const bulkCancelRef = useRef(false);
  // R159dd · Feb 2026 — Resizable columns. Widths are stored (in px)
  // per workspace in localStorage so a user's layout survives reloads.
  // `null` = auto (let the browser size it). A small drag-handle on the
  // right edge of each header captures mousedown and updates the width
  // live via document-level listeners.
  const COL_KEYS = ["title", "assignee_name", "project_name", "brand_name", "client_name", "est", "paid", "balance", "est_hours", "manual_hours", "timesheet_hours", "total_hours", "budget", "billing", "status", "due_date", "actions"];
  const COL_DEFAULTS = { title: 260, assignee_name: 140, project_name: 180, brand_name: 140, client_name: 140, est: 110, paid: 110, balance: 110, est_hours: 100, manual_hours: 110, timesheet_hours: 110, total_hours: 100, budget: 110, billing: 130, status: 110, due_date: 110, actions: 110 };
  const colWidthsKey = wsId ? `co_finance_colw_${wsId}` : null;
  const [colWidths, setColWidths] = useState(() => {
    if (!colWidthsKey) return { ...COL_DEFAULTS };
    try {
      const raw = localStorage.getItem(colWidthsKey);
      return raw ? { ...COL_DEFAULTS, ...JSON.parse(raw) } : { ...COL_DEFAULTS };
    } catch { return { ...COL_DEFAULTS }; }
  });
  const resizeRef = useRef(null); // { key, startX, startWidth }
  useEffect(() => {
    const onMove = (e) => {
      const r = resizeRef.current;
      if (!r) return;
      const dx = e.clientX - r.startX;
      const next = Math.max(60, r.startWidth + dx);
      setColWidths((cur) => ({ ...cur, [r.key]: next }));
    };
    const onUp = () => {
      if (!resizeRef.current) return;
      resizeRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);
  useEffect(() => {
    if (!colWidthsKey) return;
    try { localStorage.setItem(colWidthsKey, JSON.stringify(colWidths)); } catch { /* ignore */ }
  }, [colWidths, colWidthsKey]);
  const startResize = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { key, startX: e.clientX, startWidth: colWidths[key] || COL_DEFAULTS[key] || 120 };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const resetColWidths = () => setColWidths({ ...COL_DEFAULTS });
  const resetOne = (key) => () => setColWidths((cur) => ({ ...cur, [key]: COL_DEFAULTS[key] }));
  const toggleIn = (setter) => (val) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(val)) next.delete(val); else next.add(val);
    return next;
  });

  // R159aa · Feb 2026 — Compute a synthetic billing-state label per
  // task so the multi-select is a single, easy-to-read dropdown.
  const billingState = (t) => {
    if (t?.paid) return "Paid";
    if (t?.billed) return "Billed";
    if (t?.billable) return "Billable";
    return "Not billable";
  };

  const projectById = useMemo(() => new Map((projects || []).map((p) => [p.id, p])), [projects]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (tasks || []).map((t) => {
      const p = projectById.get(t.project_id) || {};
      const est = Number(t.est_amount) || 0;
      const paid = Number(t.paid_amount) || 0;
      const bal = est - paid;
      const est_hours = Number(t.est_hours) || 0;
      const manual_hours = Number(t.manual_effort_hours) || 0;
      const timesheet_hours = Number(t.logged_hours) || Number(t.timesheet_hours) || 0;
      // R159dd · Feb 2026 — Aligned with `effort_service.annotate_task_effort`
      // and the Tasks > List columns: Total = Manual + Timesheet. The old
      // formula summed `actual_hours` too, but that field is a legacy
      // accumulator we're no longer surfacing (removed from the report per
      // R159dd). Budget = Est − Total (positive means under budget).
      const total_hours = Number(t.total_effort_hours);
      const total = Number.isFinite(total_hours) && total_hours > 0
        ? total_hours
        : manual_hours + timesheet_hours;
      return {
        id: t.id,
        title: t.title || "(untitled)",
        assignee_id: t.assignee_id || null,
        assignee_name: t.assignee_name || "",
        project_id: t.project_id,
        project_name: p.name || t.project_name || "—",
        brand_id: t.brand_id || p.brand_id || null,
        brand_name: p.brand_name || "—",
        client_id: p.client_id || null,
        client_name: p.client_name || "—",
        currency: t.currency || "INR",
        est,
        paid,
        balance: bal,
        est_hours,
        manual_hours,
        timesheet_hours,
        total_hours: total,
        budget: est_hours > 0 ? est_hours - total : 0,
        has_budget: est_hours > 0,
        status: t.status || "—",
        billable: !!t.billable,
        billed: !!t.billed,
        paid_flag: !!t.paid,
        billing_state: billingState(t),
        due_date: t.due_date || "",
      };
    }).filter((r) => {
      // R159aa · Feb 2026 — Report now surfaces EVERY task (not just
      // those with finance data). Filters below still let the user
      // slice down when they want.
      if (statusFilter.size > 0 && !statusFilter.has(r.status)) return false;
      if (currencyFilter.size > 0 && !currencyFilter.has(r.currency)) return false;
      if (brandFilter.size > 0 && !brandFilter.has(r.brand_name)) return false;
      if (billingFilter.size > 0 && !billingFilter.has(r.billing_state)) return false;
      if (clientFilter.size > 0 && !clientFilter.has(r.client_name)) return false;
      if (projectFilter.size > 0 && !projectFilter.has(r.project_name)) return false;
      if (!term) return true;
      return (
        r.title.toLowerCase().includes(term)
        || (r.assignee_name || "").toLowerCase().includes(term)
        || r.project_name.toLowerCase().includes(term)
        || r.brand_name.toLowerCase().includes(term)
        || r.client_name.toLowerCase().includes(term)
      );
    });
  }, [tasks, projectById, q, statusFilter, currencyFilter, brandFilter, billingFilter, clientFilter, projectFilter]);

  const facets = useMemo(() => {
    const status = new Set(), currency = new Set(), brand = new Set(), billing = new Set(), client = new Set(), project = new Set();
    (tasks || []).forEach((t) => {
      status.add(t.status || "—");
      currency.add(t.currency || "INR");
      const p = projectById.get(t.project_id) || {};
      brand.add(p.brand_name || "—");
      client.add(p.client_name || "—");
      project.add(p.name || "—");
      billing.add(billingState(t));
    });
    return {
      status: [...status].sort(),
      currency: [...currency].sort(),
      brand: [...brand].sort(),
      client: [...client].sort(),
      project: [...project].sort(),
      // R159gg · Feb 2026 — Always expose all four billing states in
      // the facet even when no task currently has that state, so users
      // can proactively filter "Billed" or "Paid" without having to
      // first create tasks in that state.
      billing: ["Not billable", "Billable", "Billed", "Paid"],
    };
  }, [tasks, projectById]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      return sortDir === "asc"
        ? String(va || "").localeCompare(String(vb || ""))
        : String(vb || "").localeCompare(String(va || ""));
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  // Group + subtotal (per currency).
  const grouped = useMemo(() => {
    if (groupBy === "none") return null;
    const key = groupBy === "project" ? "project_name" : groupBy === "brand" ? "brand_name" : "client_name";
    const map = new Map();
    sortedRows.forEach((r) => {
      const k = r[key] || "—";
      const g = map.get(k) || { label: k, rows: [], perCurrency: {} };
      g.rows.push(r);
      const c = r.currency || "INR";
      const pc = g.perCurrency[c] || { est: 0, paid: 0, balance: 0 };
      pc.est += r.est;
      pc.paid += r.paid;
      pc.balance += r.balance;
      g.perCurrency[c] = pc;
      map.set(k, g);
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [sortedRows, groupBy]);

  // Grand-totals — per currency — always visible in the footer.
  const grandTotals = useMemo(() => {
    const acc = {};
    sortedRows.forEach((r) => {
      const c = r.currency || "INR";
      const pc = acc[c] || { est: 0, paid: 0, balance: 0 };
      pc.est += r.est;
      pc.paid += r.paid;
      pc.balance += r.balance;
      acc[c] = pc;
    });
    return acc;
  }, [sortedRows]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  const exportRows = () => sortedRows.map((r) => ({
    Task: r.title,
    Assignee: r.assignee_name || "",
    Project: r.project_name,
    Brand: r.brand_name,
    Client: r.client_name,
    Currency: r.currency,
    Est: r.est,
    Paid: r.paid,
    Balance: r.balance,
    "Est Effort (h)": r.est_hours,
    "Manual Effort (h)": r.manual_hours,
    "Timesheet Effort (h)": r.timesheet_hours,
    "Total Effort (h)": r.total_hours,
    "Budget Delta (h)": r.has_budget ? r.budget : "",
    Billing: r.billing_state,
    Status: r.status,
    Due: r.due_date,
  }));

  const exportCSV = () => {
    const rows = exportRows();
    if (rows.length === 0) { toast.error("No rows to export"); return; }
    const header = Object.keys(rows[0]);
    const body = rows.map((r) => header.map((k) => r[k]));
    downloadCSV([header, ...body], `finance-report-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportXLSX = () => {
    const rows = exportRows();
    if (rows.length === 0) { toast.error("No rows to export"); return; }
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Finance");
    XLSX.writeFile(wb, `finance-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Exported ${rows.length} rows to Excel`);
  };

  const renderH = (k, children, right) => (
    <SortHead sortKey={k} activeKey={sortKey} sortDir={sortDir} onSet={setSort} right={right}>{children}</SortHead>
  );

  const wrapCls = wrap ? "whitespace-normal break-words" : "truncate max-w-[220px]";
  const setBillingInline = (task, patch) => {
    if (!onPatchTask) return;
    onPatchTask(task.id, patch);
  };

  const renderRow = (r) => (
    <tr
      key={r.id}
      className="border-b border-border hover:bg-neutral-50"
      data-testid={`finance-report-row-${r.id}`}
    >
      <InlineCell taskId={r.id} field="title" value={r.title} className={`px-2 py-1.5 text-[12px] ${wrapCls}`} cellTestid={`finance-report-cell-title-${r.id}`} onPatchTask={onPatchTask} />
      {/* R159dd · Feb 2026 — Assignee column mirrors the Tasks > List
          view. Read-only for now (assignee edits still go through the
          Task Detail dialog so RBAC/audit fires correctly). */}
      <td className={`px-2 py-1.5 text-[11px] text-muted-foreground ${wrap ? "whitespace-normal" : "truncate max-w-[140px]"}`} data-testid={`finance-report-cell-assignee-${r.id}`}>
        {r.assignee_name || <span className="text-neutral-300">—</span>}
      </td>
      <td className={`px-2 py-1.5 text-[11px] text-muted-foreground ${wrap ? "whitespace-normal" : "truncate max-w-[160px]"}`}>{r.project_name}</td>
      <td className={`px-2 py-1.5 text-[11px] text-muted-foreground ${wrap ? "whitespace-normal" : "truncate max-w-[140px]"}`}>{r.brand_name}</td>
      <td className={`px-2 py-1.5 text-[11px] text-muted-foreground ${wrap ? "whitespace-normal" : "truncate max-w-[140px]"}`}>{r.client_name}</td>
      <InlineCell taskId={r.id} field="est_amount" value={r.est} kind="number"
        className="px-2 py-1.5 text-[11px] text-right font-mono"
        render={(v) => fmtMoney(v, r.currency)}
        cellTestid={`finance-report-cell-est-${r.id}`} onPatchTask={onPatchTask} />
      <InlineCell taskId={r.id} field="paid_amount" value={r.paid} kind="number"
        className="px-2 py-1.5 text-[11px] text-right font-mono"
        render={(v) => fmtMoney(v, r.currency)}
        cellTestid={`finance-report-cell-paid-${r.id}`} onPatchTask={onPatchTask} />
      <td className={`px-2 py-1.5 text-[11px] text-right font-mono ${r.balance > 0 ? "text-rose-700" : r.balance < 0 ? "text-emerald-700" : ""}`}>{fmtMoney(r.balance, r.currency)}</td>
      <InlineCell taskId={r.id} field="est_hours" value={r.est_hours} kind="number"
        className="px-2 py-1.5 text-[11px] text-right font-mono"
        render={(v) => v ? `${v}h` : "—"}
        cellTestid={`finance-report-cell-esth-${r.id}`} onPatchTask={onPatchTask} />
      {/* R159dd · Feb 2026 — Manual effort is inline-editable and syncs
          two-way with the task doc via `onPatchTask` (PATCH /tasks/{id}).
          The Task Detail dialog reads the same `manual_effort_hours`
          field so an edit here shows up there on next open, and vice
          versa. */}
      <InlineCell taskId={r.id} field="manual_effort_hours" value={r.manual_hours} kind="number"
        className="px-2 py-1.5 text-[11px] text-right font-mono"
        render={(v) => v ? `${v}h` : "—"}
        cellTestid={`finance-report-cell-manualh-${r.id}`} onPatchTask={onPatchTask} />
      <td className="px-2 py-1.5 text-[11px] text-right font-mono">{r.timesheet_hours ? `${r.timesheet_hours}h` : "—"}</td>
      <td className={`px-2 py-1.5 text-[11px] text-right font-mono font-semibold ${r.est_hours > 0 && r.total_hours > r.est_hours ? "text-rose-700" : ""}`} data-testid={`finance-report-cell-totalh-${r.id}`}>{r.total_hours ? `${r.total_hours.toFixed(2)}h` : "—"}</td>
      {/* R159dd · Feb 2026 — Budget = Est − Total. Positive = under
          budget (green chip, hours saved). Negative = over budget
          (red chip, hours blown). Mirrors Tasks > List behaviour. */}
      <td className="px-2 py-1.5 text-right whitespace-nowrap" data-testid={`finance-report-cell-budget-${r.id}`}>
        {!r.has_budget ? (
          <span className="text-neutral-400 text-[10px]" title="No estimate set">No est.</span>
        ) : r.total_hours === 0 ? (
          <span className="text-neutral-300 text-[10px]" title={`Budget ${r.est_hours}h · nothing logged yet`}>—</span>
        ) : r.budget >= 0 ? (
          <span className="inline-block border border-emerald-500 bg-emerald-50 text-emerald-700 px-1.5 text-[10px] font-mono font-bold" title={`Under budget by ${r.budget.toFixed(2)}h · Est ${r.est_hours}h · Total ${r.total_hours.toFixed(2)}h`}>−{r.budget.toFixed(2)}h</span>
        ) : (
          <span className="inline-block border border-rose-500 bg-rose-50 text-rose-700 px-1.5 text-[10px] font-mono font-bold" title={`Over budget by ${Math.abs(r.budget).toFixed(2)}h · Est ${r.est_hours}h · Total ${r.total_hours.toFixed(2)}h`}>+{Math.abs(r.budget).toFixed(2)}h</span>
        )}
      </td>
      <td className="px-2 py-1.5">
        <select
          value={r.billing_state}
          onChange={(e) => {
            const v = e.target.value;
            const patch = v === "Not billable" ? { billable: false, billed: false, paid: false }
              : v === "Billable" ? { billable: true, billed: false, paid: false }
              : v === "Billed" ? { billable: true, billed: true, paid: false }
              : { billable: true, billed: true, paid: true };
            setBillingInline({ id: r.id }, patch);
          }}
          className={`h-6 border rounded-none text-[10px] uppercase tracking-widest px-1 ${
            r.billing_state === "Paid" ? "border-purple-400 bg-purple-50 text-purple-800"
            : r.billing_state === "Billed" ? "border-sky-400 bg-sky-50 text-sky-800"
            : r.billing_state === "Billable" ? "border-emerald-400 bg-emerald-50 text-emerald-800"
            : "border-neutral-200 bg-neutral-50 text-neutral-500"
          }`}
          data-testid={`finance-report-billing-${r.id}`}
        >
          <option value="Not billable">Not billable</option>
          <option value="Billable">Billable</option>
          <option value="Billed">Billed</option>
          <option value="Paid">Paid</option>
        </select>
      </td>
      <InlineCell taskId={r.id} field="status" value={r.status} kind="select"
        options={[
          /* R159gg · Feb 2026 — Friendly labels for the status
             dropdown. Values still stored snake_case for API
             compatibility — only the displayed label changes. */
          { value: "open", label: "Open" },
          { value: "in_progress", label: "In Progress" },
          { value: "review", label: "Review" },
          { value: "blocked", label: "Blocked" },
          { value: "done", label: "Done" },
        ]}
        className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground"
        cellTestid={`finance-report-cell-status-${r.id}`} onPatchTask={onPatchTask} />
      <InlineCell taskId={r.id} field="due_date" value={r.due_date} kind="date"
        className="px-2 py-1.5 text-[10px] text-muted-foreground"
        cellTestid={`finance-report-cell-due-${r.id}`} onPatchTask={onPatchTask} />
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        {onOpenFinance && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenFinance({ id: r.id, title: r.title, est_amount: r.est, paid_amount: r.paid, currency: r.currency }); }}
            className="inline-flex items-center justify-center w-6 h-6 border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 mr-1"
            title="Open Task Finance dialog"
            data-testid={`finance-report-finance-${r.id}`}
          >
            <Wallet className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenTask?.(r.id); }}
          className="inline-flex items-center justify-center w-6 h-6 border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-100 hover:text-primary mr-1"
          title="Edit task"
          data-testid={`finance-report-edit-${r.id}`}
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenNotes?.({ scope: "task", id: r.id, label: r.title }); }}
          className="inline-flex items-center justify-center w-6 h-6 border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
          title="Open accounts thread for this task"
          data-testid={`finance-report-notes-${r.id}`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-none w-[95vw] h-[95vh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="finance-report-dialog"
      >
        <DialogHeader className="p-4 border-b border-border shrink-0">
          <DialogTitle className="text-lg">Finance Report</DialogTitle>
          <DialogDescription className="text-xs">
            Task-level estimated / paid / balance across the visible board — grouped, sorted and exportable.
          </DialogDescription>
        </DialogHeader>
        {/* R159cc · Feb 2026 — Preset tabs strip + Bulk-billing toolbar.
            Tabs restore a saved filter combo in one click; Save current
            captures the live filter state under a name. */}
        <div className="flex items-center gap-1 px-4 py-1.5 border-b border-border shrink-0 bg-white flex-wrap" data-testid="finance-report-preset-strip">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 mr-1">Presets</span>
          {presets.length === 0 && <span className="text-[11px] italic text-muted-foreground">None yet — apply filters and click Save to add one</span>}
          {presets.map((p) => (
            <span key={p.name} className={`inline-flex items-center border ${activePreset === p.name ? "bg-amber-500 text-white border-amber-600" : "border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100"} h-7 rounded-none text-[10px] uppercase tracking-widest`}>
              <button
                type="button"
                onClick={() => applyPreset(p)}
                className="px-2 h-full"
                data-testid={`finance-report-preset-${p.name.replace(/[^A-Za-z0-9_-]+/g, "_")}`}
              >
                {p.name}
              </button>
              <button
                type="button"
                onClick={() => deletePreset(p.name)}
                className="px-1 h-full border-l border-current/40 hover:bg-black/10"
                title="Delete preset"
                data-testid={`finance-report-preset-del-${p.name.replace(/[^A-Za-z0-9_-]+/g, "_")}`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={saveCurrentAsPreset}
            className="h-7 px-2 border border-neutral-300 bg-white text-neutral-700 text-[10px] uppercase tracking-widest hover:bg-neutral-50"
            data-testid="finance-report-preset-save"
          >
            + Save current
          </button>
          <div className="ml-auto flex items-center gap-1 flex-wrap" data-testid="finance-report-bulk-toolbar">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Bulk update {sortedRows.length}</span>
            {["Not billable", "Billable", "Billed", "Paid"].map((state) => (
              <button
                key={state}
                type="button"
                disabled={bulkBusy || sortedRows.length === 0 || !onPatchTask}
                onClick={async () => {
                  if (!onPatchTask) return;
                  if (!window.confirm(`Set ${sortedRows.length} visible task${sortedRows.length === 1 ? "" : "s"} to "${state}"?`)) return;
                  bulkCancelRef.current = false;
                  setBulkBusy(true);
                  setBulkProgress({ done: 0, total: sortedRows.length, state });
                  const patch = state === "Not billable" ? { billable: false, billed: false, paid: false }
                    : state === "Billable" ? { billable: true, billed: false, paid: false }
                    : state === "Billed" ? { billable: true, billed: true, paid: false }
                    : { billable: true, billed: true, paid: true };
                  let ok = 0, fail = 0, cancelled = 0;
                  for (let i = 0; i < sortedRows.length; i++) {
                    if (bulkCancelRef.current) { cancelled = sortedRows.length - i; break; }
                    const r = sortedRows[i];
                    try { await onPatchTask(r.id, patch); ok += 1; } catch { fail += 1; }
                    setBulkProgress({ done: i + 1, total: sortedRows.length, state });
                  }
                  setBulkBusy(false);
                  setBulkProgress(null);
                  if (cancelled) toast.info(`Cancelled · ${ok} updated · ${cancelled} skipped${fail ? " · " + fail + " failed" : ""}`);
                  else if (fail === 0) toast.success(`Updated ${ok} task${ok === 1 ? "" : "s"} → ${state}`);
                  else toast.error(`Updated ${ok} · failed ${fail}`);
                }}
                className={`h-7 px-2 border rounded-none text-[10px] uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed ${
                  state === "Paid" ? "border-purple-400 bg-purple-50 text-purple-800 hover:bg-purple-100"
                  : state === "Billed" ? "border-sky-400 bg-sky-50 text-sky-800 hover:bg-sky-100"
                  : state === "Billable" ? "border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
                data-testid={`finance-report-bulk-${state.replace(/\s+/g, "_")}`}
              >
                {state}
              </button>
            ))}
            {/* R159ee · Feb 2026 — Live progress + cancel while the
                bulk loop runs. Renders only when a bulk update is
                actively in flight. */}
            {bulkProgress && (
              <div className="inline-flex items-center gap-2 border border-amber-400 bg-amber-50 px-2 h-7 rounded-none" data-testid="finance-report-bulk-progress">
                <span className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
                  {bulkProgress.state} · {bulkProgress.done}/{bulkProgress.total}
                </span>
                <div className="w-24 h-1.5 bg-amber-200 relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-amber-600 transition-[width] duration-150"
                    style={{ width: `${Math.round((bulkProgress.done / Math.max(1, bulkProgress.total)) * 100)}%` }}
                    data-testid="finance-report-bulk-progress-bar"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { bulkCancelRef.current = true; }}
                  className="h-5 px-1.5 border border-rose-400 bg-white text-rose-700 hover:bg-rose-50 text-[10px] uppercase tracking-widest"
                  title="Cancel · remaining tasks won't be updated"
                  data-testid="finance-report-bulk-cancel"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border shrink-0 bg-neutral-50/70 flex-wrap">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search task · project · brand · client"
            className="h-8 max-w-sm rounded-none"
            data-testid="finance-report-search"
          />
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="h-8 border border-input bg-background px-2 rounded-none text-xs"
            data-testid="finance-report-groupby"
          >
            <option value="none">Group · None (task rows)</option>
            <option value="project">Group · Project</option>
            <option value="brand">Group · Brand</option>
            <option value="client">Group · Client</option>
          </select>
          {/* R159z · Feb 2026 — Multi-select filter chip rows. Empty
              set = any; clicking a chip toggles it into the filter. */}
          <FacetFilter label="Status" values={facets.status} selected={statusFilter} onToggle={toggleIn(setStatusFilter)} testid="finance-report-filter-status" />
          <FacetFilter label="Currency" values={facets.currency} selected={currencyFilter} onToggle={toggleIn(setCurrencyFilter)} testid="finance-report-filter-currency" />
          <FacetFilter label="Brand" values={facets.brand} selected={brandFilter} onToggle={toggleIn(setBrandFilter)} testid="finance-report-filter-brand" searchable />
          <FacetFilter label="Client" values={facets.client} selected={clientFilter} onToggle={toggleIn(setClientFilter)} testid="finance-report-filter-client" searchable />
          <FacetFilter label="Project" values={facets.project} selected={projectFilter} onToggle={toggleIn(setProjectFilter)} testid="finance-report-filter-project" searchable />
          <FacetFilter label="Billing" values={facets.billing} selected={billingFilter} onToggle={toggleIn(setBillingFilter)} testid="finance-report-filter-billing" />
          {(statusFilter.size > 0 || currencyFilter.size > 0 || brandFilter.size > 0 || billingFilter.size > 0 || clientFilter.size > 0 || projectFilter.size > 0) && (
            <button
              type="button"
              onClick={() => { setStatusFilter(new Set()); setCurrencyFilter(new Set()); setBrandFilter(new Set()); setBillingFilter(new Set()); setClientFilter(new Set()); setProjectFilter(new Set()); }}
              className="h-8 px-2 border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 text-[10px] uppercase tracking-widest"
              data-testid="finance-report-filter-clear"
            >
              <X className="w-3 h-3 inline -mt-0.5 mr-0.5" /> Clear filters
            </button>
          )}
          <button
            type="button"
            onClick={() => setWrap((v) => !v)}
            className={`h-8 px-2 border rounded-none text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${wrap ? "bg-neutral-800 text-white border-neutral-900" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"}`}
            title="Toggle line wrap — long task titles stay on one line when Wrap is off"
            data-testid="finance-report-wrap-toggle"
          >
            <WrapText className="w-3 h-3" /> Wrap {wrap ? "on" : "off"}
          </button>
          <button
            type="button"
            onClick={resetColWidths}
            className="h-8 px-2 border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 rounded-none text-[10px] uppercase tracking-widest"
            title="Reset all column widths to defaults"
            data-testid="finance-report-reset-widths"
          >
            Reset widths
          </button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {sortedRows.length} row{sortedRows.length === 1 ? "" : "s"}
            </span>
            <Button variant="outline" onClick={exportCSV} className="h-8 rounded-none" data-testid="finance-report-export">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" onClick={exportXLSX} className="h-8 rounded-none border-emerald-400 text-emerald-800 bg-emerald-50 hover:bg-emerald-100" data-testid="finance-report-export-xlsx">
              <Download className="w-4 h-4 mr-1" /> Excel
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="text-sm" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }} data-testid="finance-report-table">
            <colgroup>
              {COL_KEYS.map((k) => (
                <col key={k} style={{ width: `${colWidths[k] || COL_DEFAULTS[k] || 120}px` }} data-testid={`finance-report-col-${k}`} />
              ))}
            </colgroup>
            <thead className="bg-neutral-50 sticky top-0 z-10">
              <tr className="border-b border-border">
                <th className="text-left relative">{renderH("title", "Task")}<ResizeHandle onMouseDown={startResize("title")} onReset={resetOne("title")} testid="resize-title" /></th>
                <th className="text-left relative">{renderH("assignee_name", "Assignee")}<ResizeHandle onMouseDown={startResize("assignee_name")} onReset={resetOne("assignee_name")} testid="resize-assignee_name" /></th>
                <th className="text-left relative">{renderH("project_name", "Project")}<ResizeHandle onMouseDown={startResize("project_name")} onReset={resetOne("project_name")} testid="resize-project_name" /></th>
                <th className="text-left relative">{renderH("brand_name", "Brand")}<ResizeHandle onMouseDown={startResize("brand_name")} onReset={resetOne("brand_name")} testid="resize-brand_name" /></th>
                <th className="text-left relative">{renderH("client_name", "Client")}<ResizeHandle onMouseDown={startResize("client_name")} onReset={resetOne("client_name")} testid="resize-client_name" /></th>
                <th className="text-right relative">{renderH("est", "Est", true)}<ResizeHandle onMouseDown={startResize("est")} onReset={resetOne("est")} testid="resize-est" /></th>
                <th className="text-right relative">{renderH("paid", "Paid", true)}<ResizeHandle onMouseDown={startResize("paid")} onReset={resetOne("paid")} testid="resize-paid" /></th>
                <th className="text-right relative">{renderH("balance", "Balance", true)}<ResizeHandle onMouseDown={startResize("balance")} onReset={resetOne("balance")} testid="resize-balance" /></th>
                <th className="text-right relative">{renderH("est_hours", "Est Effort", true)}<ResizeHandle onMouseDown={startResize("est_hours")} onReset={resetOne("est_hours")} testid="resize-est_hours" /></th>
                <th className="text-right relative">{renderH("manual_hours", "Manual Effort", true)}<ResizeHandle onMouseDown={startResize("manual_hours")} onReset={resetOne("manual_hours")} testid="resize-manual_hours" /></th>
                <th className="text-right relative">{renderH("timesheet_hours", "Timesheet Effort", true)}<ResizeHandle onMouseDown={startResize("timesheet_hours")} onReset={resetOne("timesheet_hours")} testid="resize-timesheet_hours" /></th>
                <th className="text-right relative">{renderH("total_hours", "Total Effort", true)}<ResizeHandle onMouseDown={startResize("total_hours")} onReset={resetOne("total_hours")} testid="resize-total_hours" /></th>
                <th className="text-right relative">{renderH("budget", "Budget", true)}<ResizeHandle onMouseDown={startResize("budget")} onReset={resetOne("budget")} testid="resize-budget" /></th>
                <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground relative">Billing<ResizeHandle onMouseDown={startResize("billing")} onReset={resetOne("billing")} testid="resize-billing" /></th>
                <th className="text-left relative">{renderH("status", "Status")}<ResizeHandle onMouseDown={startResize("status")} onReset={resetOne("status")} testid="resize-status" /></th>
                <th className="text-left relative">{renderH("due_date", "Due")}<ResizeHandle onMouseDown={startResize("due_date")} onReset={resetOne("due_date")} testid="resize-due_date" /></th>
                <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground relative">Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupBy === "none" && sortedRows.map(renderRow)}
              {groupBy !== "none" && grouped && grouped.map((g) => (
                <Fragment key={`gh-${g.label}`}>
                  <tr className="bg-amber-50/70 border-b border-amber-200">
                    <td colSpan={5} className="px-2 py-1.5 text-[11px] uppercase tracking-widest font-semibold text-amber-800">
                      {g.label} <span className="opacity-60">· {g.rows.length}</span>
                    </td>
                    <td className="px-2 py-1.5 text-[11px] text-right font-mono">
                      {Object.entries(g.perCurrency).map(([c, v]) => <div key={c}>{fmtMoney(v.est, c)}</div>)}
                    </td>
                    <td className="px-2 py-1.5 text-[11px] text-right font-mono">
                      {Object.entries(g.perCurrency).map(([c, v]) => <div key={c}>{fmtMoney(v.paid, c)}</div>)}
                    </td>
                    <td className="px-2 py-1.5 text-[11px] text-right font-mono">
                      {Object.entries(g.perCurrency).map(([c, v]) => (
                        <div key={c} className={v.balance > 0 ? "text-rose-700" : v.balance < 0 ? "text-emerald-700" : ""}>{fmtMoney(v.balance, c)}</div>
                      ))}
                    </td>
                    <td colSpan={9} />
                  </tr>
                  {g.rows.map(renderRow)}
                </Fragment>
              ))}
              {sortedRows.length === 0 && (
                <tr><td colSpan={17} className="px-4 py-8 text-center text-muted-foreground text-sm">No tasks{q ? " match your search" : ""}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-border shrink-0 bg-neutral-50 px-4 py-2 flex items-center gap-4 text-xs flex-wrap" data-testid="finance-report-grand">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Grand totals</span>
          {Object.keys(grandTotals).length === 0 && <span className="text-muted-foreground">—</span>}
          {Object.entries(grandTotals).map(([c, v]) => (
            <span key={c} className="inline-flex items-center gap-2 border border-border bg-white px-2 py-1">
              <span className="text-[10px] uppercase tracking-widest font-semibold">{c}</span>
              <span className="font-mono">est {fmtMoney(v.est, c)}</span>
              <span className="opacity-40">·</span>
              <span className="font-mono">paid {fmtMoney(v.paid, c)}</span>
              <span className="opacity-40">·</span>
              <span className={`font-mono ${v.balance > 0 ? "text-rose-700" : v.balance < 0 ? "text-emerald-700" : ""}`}>bal {fmtMoney(v.balance, c)}</span>
            </span>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
